// src/components/RouteGuards/RequireSubscription.jsx
// Hard lock guard that blocks access when trial expires and no paid subscription
// ✅ Single source of truth: AccessContext only
// ✅ Prevents modal flashing during loading
// ✅ Offline mode: bypasses paywall if cached session + lastUnlocked flag exist

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAccess } from "../../contexts/AccessContext";
import SubscriptionModal from "../Modals/SubscriptionModal";

const SWITCH_TO_STUDIO_INTENT_FROM_KEY = "mudo:switchToStudioIntentFrom";

// Detect cached Supabase auth token in localStorage (offline-safe)
function hasCachedSupabaseAuthToken() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      // Supabase stores session objects in keys like "sb-{project-ref}" or "sb-{project-ref}-auth-token"
      if (k && k.startsWith("sb-")) {
        const v = localStorage.getItem(k);
        if (v && v.length > 10) {
          // Check if it contains a valid session with access_token
          try {
            const parsed = JSON.parse(v);
            if (parsed?.access_token || parsed?.currentSession?.access_token) {
              console.log('[hasCachedSupabaseAuthToken] Found valid auth in key:', k);
              return true;
            }
          } catch {
            // If not JSON, might be raw token string
            if (v.length > 50) {
              console.log('[hasCachedSupabaseAuthToken] Found token-like value in key:', k);
              return true;
            }
          }
        }
      }
    }
    console.log('[hasCachedSupabaseAuthToken] No auth token found in localStorage');
  } catch (e) {
    console.error('[hasCachedSupabaseAuthToken] Error:', e);
  }
  return false;
}

export function RequireSubscription({ children }) {
  const { loading, isUnlocked, userRow } = useAccess() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [forceShowApp, setForceShowApp] = useState(false);
  const [hasFreePass, setHasFreePass] = useState(false);

  // Debug log for subscription state
  useEffect(() => {
    console.log("[RequireSubscription] isUnlocked:", isUnlocked, "userRow:", userRow, "loading:", loading);
  }, [isUnlocked, userRow, loading]);

  // ACTIVE SUBSCRIPTION BYPASS: Allow entry if Stripe says subscription is active
  // This fixes the immediate issue while we resolve the root cause (access_until mismatch)
  const subscriptionStatus = userRow?.subscription?.status;
  const hasActiveSub = subscriptionStatus === "active";
  const canEnterStudio = isUnlocked || hasActiveSub;

  useEffect(() => {
    console.log("[RequireSubscription] status:", subscriptionStatus, "hasActiveSub:", hasActiveSub, "canEnterStudio:", canEnterStudio);
  }, [subscriptionStatus, hasActiveSub, canEnterStudio]);

  // Delay showing loading screen to avoid flash on fast connections
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => setShowLoadingScreen(true), 300);
      return () => clearTimeout(timer);
    } else {
      setShowLoadingScreen(false);
    }
  }, [loading]);

  // Safety timeout: if loading takes more than 10 seconds, force show the app
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        console.warn('[RequireSubscription] Loading timeout - forcing app to show');
        setForceShowApp(true);
      }, 10000);
      return () => clearTimeout(timeout);
    } else {
      setForceShowApp(false);
    }
  }, [loading]);

  // Track online/offline state
  useEffect(() => {
    const handleOnline = () => {
      console.log('[RequireSubscription] 🟢 ONLINE event fired');
      setIsOnline(true);
    };
    const handleOffline = () => {
      console.log('[RequireSubscription] 🔴 OFFLINE event fired');
      setIsOnline(false);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Log initial state
    console.log('[RequireSubscription] Initial navigator.onLine:', navigator.onLine);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    try {
      setHasFreePass(localStorage.getItem("mudo:freePlanSelected") === "1");
    } catch {}
  }, []);

  // Strict offline bypass: check if user was previously unlocked OR is currently unlocked (from cache)
  const hasLastUnlocked = localStorage.getItem("mudo:lastUnlocked") === "1";
  const hasCachedAuth = hasCachedSupabaseAuthToken();
  const offlineBypass = !isOnline && (hasLastUnlocked || isUnlocked) && hasCachedAuth;

  // Debug logging for offline bypass - always log when offline
  useEffect(() => {
    if (!isOnline) {
      console.log('[RequireSubscription] ===== OFFLINE MODE DETECTED =====');
      console.log('[RequireSubscription] Offline bypass check:', {
        isOnline,
        hasLastUnlocked,
        hasCachedAuth,
        isUnlocked,
        loading,
        offlineBypass,
        'Final decision': offlineBypass ? '✅ BYPASS ACTIVE - Show dashboard' : '❌ BYPASS BLOCKED - Show paywall'
      });
      // Log all localStorage keys for debugging
      console.log('[RequireSubscription] All localStorage keys:', 
        Object.keys(localStorage).filter(k => k.includes('sb-') || k.includes('mudo'))
      );
    }
  }, [isOnline, hasLastUnlocked, hasCachedAuth, isUnlocked, offlineBypass, loading]);

  // LOADING GUARD: Prevent premature gating before access state is loaded
  // This ensures we don't show the subscription modal or redirect while still loading
  // Return null immediately if loading (before the delayed loading screen appears)
  if (loading && !offlineBypass && !forceShowApp) {
    // Don't render anything during initial load to prevent flash
    // The delayed loading screen (below) will appear after 300ms if still loading
    return null;
  }

  // State 1: While loading access state, show loading screen (prevents flash)
  // Only show after 300ms delay to avoid flash on fast connections
  // Force show app after 10s timeout to prevent infinite loading
  if (loading && !offlineBypass && showLoadingScreen && !forceShowApp) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin"></div>
          <p className="text-white/60 text-sm">Checking access...</p>
        </div>
      </div>
    );
  }

  // State 2: Offline mode - allow access if we can prove cached credentials exist
  if (offlineBypass) {
    return (
      <>
        {/* Offline banner */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-orange-500/90 backdrop-blur-sm text-white px-4 py-2 text-center text-sm font-medium">
          ⚠️ Offline mode — Limited features
        </div>
        <div className="pt-10">
          {children}
        </div>
      </>
    );
  }

  // State 2b: Free plan override selected — allow access even if locked
  if (!canEnterStudio && hasFreePass) {
    return <>{children}</>;
  }

  // State 3: When not unlocked and online (or offline without cached credentials), show subscription modal
  if (!canEnterStudio) {
    let intentFrom = "";
    let role = "";
    try {
      intentFrom = localStorage.getItem(SWITCH_TO_STUDIO_INTENT_FROM_KEY) || "";
      role = localStorage.getItem("mudo:userRole") || "";
    } catch {}

    const isCeramicsNamespace = String(location?.pathname || "").startsWith("/ceramics/");
    const visitorFallback = isCeramicsNamespace ? "/ceramics/discover" : "/discover";

    const allowDismiss = Boolean(intentFrom) || role === "visitor";

    const handleDismiss = () => {
      try {
        localStorage.removeItem(SWITCH_TO_STUDIO_INTENT_FROM_KEY);
      } catch {}
      // Only force visitor mode if user clearly came from visitor intent (started in /discover)
      const wasVisitorIntentSwitch = intentFrom && (intentFrom.startsWith("/discover") || intentFrom.startsWith("/ceramics/discover"));
      if (wasVisitorIntentSwitch) {
        try {
          localStorage.setItem("mudo:userRole", "visitor");
        } catch {}
      }

      const target = intentFrom || visitorFallback;
      navigate(target, { replace: true });
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black" style={{ pointerEvents: "none" }}>
        <div className="max-w-4xl w-full p-8" style={{ pointerEvents: "auto" }}>
          {/* Trial expired banner */}
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-6 mb-6 text-center">
            <h2 className="text-2xl font-bold text-orange-400 mb-2">
              Your access has ended
            </h2>
            <p className="text-white/80">
              Choose a plan to continue using Mudo, or enter a promo code for extended access.
            </p>
          </div>

          {/* Subscription modal (embedded, no close button) */}
          <SubscriptionModal
            isOpen={true}
            onClose={allowDismiss ? handleDismiss : undefined}
            onSelectPlan={(selectedPlan) => {
              console.log("[RequireSubscription] Plan selected:", selectedPlan);
              if (selectedPlan === "free") {
                try {
                  localStorage.setItem("mudo:freePlanSelected", "1");
                } catch {}
                // Refresh entitlements (will call ensure_trial_started and re-read access)
                window.dispatchEvent?.(new Event("mudo:entitlements-refresh"));

                // Keep the user in the Studio experience when they choose Free
                try {
                  localStorage.setItem("mudo:userRole", "studio");
                  localStorage.removeItem(SWITCH_TO_STUDIO_INTENT_FROM_KEY);
                } catch {}

                setHasFreePass(true);

                // Send them to the Studio dashboard (free tier)
                navigate("/dashboard", { replace: true });
                return;
              }
            }}
            currentPlan={null}
          />

          {/* Support link */}
          <div className="text-center mt-6">
            <a
              href="mailto:support@mudoceramics.com"
              className="text-sm text-white/60 hover:text-white/80 underline"
            >
              Need help? Contact support
            </a>
          </div>
        </div>
      </div>
    );
  }

  // State 4: When unlocked, allow access
  return <>{children}</>;
}

export default RequireSubscription;
